import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { Seo } from "../components/Seo";
import { Reveal, SplitHeadline } from "../components/Motion";
import { CtaPair, ProofChip, Stripes } from "../components/Bits";
import { ScopeQuiz } from "../components/ScopeQuiz";
import { Estimator, FaqBlock, FinalCta, FinishTiers, HowItWorks, PromiseBento, ReviewsWall, ServiceArea, SliderShowcase, TwoTrack } from "../components/Sections";
import { img } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 90, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 90, damping: 18 });

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden bg-navy grain" data-testid="home-hero">
      <div className="grid-lines absolute inset-0" aria-hidden="true" />
      <span className="pointer-events-none absolute -right-10 bottom-0 select-none font-display text-[38vw] font-extrabold leading-[0.75] text-white/[0.03] lg:text-[26vw]" aria-hidden="true">30</span>
      <div className="container-x relative grid gap-10 pb-20 pt-12 sm:pt-16 lg:grid-cols-12 lg:grid-rows-[auto_auto_1fr] lg:gap-x-12 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7 lg:row-start-1">
          <Reveal y={10} className="flex items-center gap-3">
            <Stripes className="h-3.5 w-10" animate delay={0.05} />
            <p className="eyebrow text-slate-200" data-testid="hero-eyebrow">Toronto & GTA · 30 Years · Licensed & Insured</p>
          </Reveal>
          <SplitHeadline lines={["Speed & quality,", "never a", "compromise."]} accentLast delay={0.2} testId="hero-title"
            className="mt-8 text-[2.6rem] font-extrabold leading-[0.92] tracking-[-0.035em] text-white sm:text-7xl lg:text-[4.9rem] xl:text-[5.8rem]" />
          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-testid="hero-subhead">
              From crisp lines and smooth finishes, to compliant fire retardant coatings, we deliver premium results on time, on budget.
            </p>
          </Reveal>
        </div>

        <div className="relative lg:col-span-5 lg:col-start-8 lg:row-span-3 lg:row-start-1">
          <div className="relative" style={{ perspective: 1400 }}>
            <motion.div style={{ rotateX: rotX, rotateY: rotY }}
              initial={reduce ? false : { clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }}
              transition={{ duration: 1.3, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative ml-auto h-44 w-[88%] overflow-hidden rounded-md sm:h-56 lg:h-64">
              <motion.img style={{ y: photoY }} src={img.heroRoom} alt="Freshly painted living room with crisp lines and a smooth finish" className="absolute inset-0 h-[140%] w-full -translate-y-[12%] object-cover" />
              <div className="absolute inset-0 bg-navy/20" />
              <span className="absolute bottom-3 right-3 rounded-full bg-navy/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">Finished · on time</span>
            </motion.div>
            <Stripes className="absolute -left-1 top-6 h-28 w-8 sm:h-36" animate delay={0.5} />
          </div>
          <motion.div initial={reduce ? false : { opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 -mt-16 sm:-mt-20">
            <ScopeQuiz />
          </motion.div>
        </div>

        <Reveal delay={0.75} className="lg:col-span-7 lg:col-start-1 lg:row-start-2 lg:self-start">
          <CtaPair testPrefix="hero" />
          <div className="mt-6"><ProofChip /></div>
        </Reveal>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <>
      <Seo title="Amora Painting | Toronto & GTA Painters — Speed & Quality, 30 Years" description="Toronto painting company. Crisp lines, smooth finishes and compliant fire retardant coatings — premium results on time, on budget. 30 years across the GTA, cottage country and Ontario. Free Colour & Scope Visit." />
      <Hero />
      <PromiseBento />
      <TwoTrack />
      <HowItWorks />
      <SliderShowcase />
      <FinishTiers />
      <Estimator />
      <ReviewsWall compact />
      <FaqBlock />
      <ServiceArea />
      <FinalCta />
    </>
  );
}
